import { gql } from "@apollo/client";
import { Box, Flex, IconButton, Text, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { LuArrowDown, LuArrowUp, LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthContext } from "../auth";
import { Order_By, useBulkDiscountItemsMutation, useItemsQuery } from "../graphql/generated/apollo";
import { useShopContext } from "../shop";
import { calculateDiscountedPrice } from "../utils/discount";
import { BulkDiscountForm } from "./BulkDiscountForm";
import { GeneralModal } from "./GeneralModal";
import { ListingCard } from "./ListingCard";
import { SearchBar } from "./SearchBar";
import { Button } from "./ui/button";
import { toaster } from "./ui/toaster";
import { Tooltip } from "./ui/tooltip";

const LIMIT_PER_PAGE = 20;
const PAGE_BUTTON_RANGE = 2;

gql`
  query Items($shop_id: uuid!, $cond: [items_bool_exp!] = {}, $order_by: [items_order_by!] = {created_at: desc}, $limit: Int = 200, $offset: Int = 0) {
    items(
      where: {
        shop_id: { _eq: $shop_id }
        _and: $cond
      }
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      id
      name
      price
      compare_at_price
      exhibitions {
        id
        platform
        platform_item_id
        status
        error_message
      }
      variants(order_by: { position: asc }) {
        id
        name
        stock_quantity
        position
      }
      images(order_by: [{ position: asc }, { created_at: asc }]) {
        id
        mime_type
        path
        created_at
      }
      item_status {
        name
      }
      status
      created_at
      updated_at
    }
    items_aggregate(where: { shop_id: { _eq: $shop_id }, _and: $cond }) {
      aggregate {
        count
      }
    }
  }
`;

type BulkActionBase = {
  selectedItemIds: string[];
  showConfirmModal: boolean;
  isProcessing: boolean;
};

type BulkActionMode =
  | { type: "none" }
  | (BulkActionBase & {
      type: "discount";
      discountAmount?: number;
      discountType: "rate" | "amount";
      discountRate?: number;
    });

export function ListedItemsTab() {
  return <ItemList />;
}

function searchParam(term: string, statuses: string[] = ["ACTIVE", "DRAFT", "ARCHIVED"]) {
  return term
    ? [
        {
          _or: [
            {
              name: { _ilike: `%${term}%` },
            },
            {
              description: { _ilike: `%${term}%` },
            },
          ],
        },
        {
          status: { _in: statuses },
        },
      ]
    : [
        {
          status: { _in: statuses },
        },
      ];
}

function orderByParam(asc: boolean) {
  return asc ? { created_at: Order_By.Asc } : { created_at: Order_By.Desc };
}

function ItemList() {
  const { user } = useAuthContext();
  const { currentShopId } = useShopContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const query = searchParams.get("query") ?? "";
  const order = searchParams.get("order") ?? "desc";
  const ascending = order === "asc";

  const offset = (page - 1) * LIMIT_PER_PAGE;

  // 初期表示用のデータを取得し、検索時には検索結果を取得する。かつページネーションも行う
  const { data, loading, error } = useItemsQuery({
    variables: {
      shop_id: currentShopId ?? "",
      cond: searchParam(query, ["ACTIVE", "DRAFT"]),
      order_by: orderByParam(order === "asc"),
      limit: LIMIT_PER_PAGE,
      offset: offset < LIMIT_PER_PAGE ? 0 : offset,
    },
    notifyOnNetworkStatusChange: true,
    skip: !user || !currentShopId,
  });

  const [bulkActionMode, setBulkActionMode] = useState<BulkActionMode>({ type: "none" });
  const [bulkDiscountItemsMutation] = useBulkDiscountItemsMutation();

  if (!currentShopId) return <p>ショップが選択されていません</p>;
  if (error) return <p>Error : {error.message}</p>;

  const totalCount = data?.items_aggregate?.aggregate?.count ?? 0;
  const totalPage = Math.ceil(totalCount / LIMIT_PER_PAGE);
  const movePage = (to: number, scrollToTop?: boolean, query?: string, ascending?: boolean) => {
    // 新規読み込みのあとに即ページ切り替えをする可能性があるので最終ページ+1まで許容
    if (to < 1 || totalPage + 1 < to) {
      return;
    }

    // URLパラメータを更新
    const params = new URLSearchParams();
    if (query) {
      params.set("query", query);
    }
    if (ascending) {
      params.set("order", "asc");
    }
    if (to && to > 1) {
      params.set("page", to.toString());
    }
    navigate(`?${params.toString()}`);
    if (scrollToTop) {
      window.scrollTo(0, 0);
    }
  };

  const handleCheck = (itemId: string, checked: boolean) => {
    if (bulkActionMode.type === "discount") {
      setBulkActionMode((prev) => {
        if (prev.type === "discount") {
          return {
            ...prev,
            selectedItemIds: checked
              ? [...prev.selectedItemIds, itemId]
              : prev.selectedItemIds.filter((id: string) => id !== itemId),
          };
        }
        return prev;
      });
    }
  };

  const handleBulkDiscountClick = () => {
    setBulkActionMode({
      type: "discount",
      selectedItemIds: [],
      discountAmount: 100,
      discountType: "rate",
      discountRate: 10,
      showConfirmModal: false,
      isProcessing: false,
    });
  };

  const handleCancel = () => {
    setBulkActionMode({ type: "none" });
  };

  const handleSetDiscount = () => {
    if (bulkActionMode.type === "discount") {
      setBulkActionMode((prev) => ({ ...prev, showConfirmModal: true }));
    }
  };

  const handleExecuteDiscount = async () => {
    if (bulkActionMode.type !== "discount") return;

    const { selectedItemIds, discountType, discountRate, discountAmount } = bulkActionMode;

    if (selectedItemIds.length === 0) {
      setBulkActionMode((prev) => ({ ...prev, showConfirmModal: false }));
      return;
    }
    if (discountType === "amount" && (!discountAmount || discountAmount <= 0)) {
      setBulkActionMode((prev) => ({ ...prev, showConfirmModal: false }));
      return;
    }
    if (discountType === "rate" && (!discountRate || discountRate <= 0)) {
      setBulkActionMode((prev) => ({ ...prev, showConfirmModal: false }));
      return;
    }

    setBulkActionMode((prev) => ({ ...prev, isProcessing: true }));
    try {
      const updates =
        data?.items
          .filter((item) => selectedItemIds.includes(item.id))
          .map((item) => ({
            where: { id: { _eq: item.id } },
            _set: {
              price: calculateDiscountedPrice(
                item.compare_at_price ?? item.price,
                discountType,
                discountRate,
                discountAmount,
              ),
              compare_at_price: item.compare_at_price ?? item.price,
            },
          })) ?? [];
      await bulkDiscountItemsMutation({
        variables: { updates },
        refetchQueries: ["Items"],
      });
      setBulkActionMode({ type: "none" });
      toaster.create({ title: "値下げを実行しました", type: "success" });
    } catch (_e) {
      toaster.create({ title: "値下げに失敗しました", type: "error" });
      setBulkActionMode((prev) => ({ ...prev, showConfirmModal: false }));
    } finally {
      setBulkActionMode((prev) => ({ ...prev, isProcessing: false }));
    }
  };

  const modalMessage = () => {
    if (bulkActionMode.type !== "discount") return "";

    const { discountType, discountRate, discountAmount, selectedItemIds } = bulkActionMode;

    if (discountType === "amount") {
      return `${discountAmount ?? 0}円値下げします。よろしいですか？（対象商品${selectedItemIds.length}件）`;
    }
    return `${discountRate ?? 0}%値下げします。よろしいですか？（対象商品${selectedItemIds.length}件）\n\n小数点以下は切り捨てになります。`;
  };

  return (
    <>
      {bulkActionMode.type === "discount" ? (
        <Flex gap={2} justifyContent="flex-end">
          <Button onClick={handleCancel} colorPalette="gray">
            キャンセル
          </Button>
        </Flex>
      ) : (
        <Flex gap={2} justifyContent="space-between">
          <Box flex={1}>
            <SearchBar
              initialValue={query}
              onSearch={async (term: string) => {
                movePage(1, true, term, ascending);
              }}
              loading={loading}
            />
          </Box>
          <Tooltip content={`登録日の${ascending ? "古い順" : "新しい順"}`}>
            <IconButton
              onClick={async () => {
                const newOrder = !ascending;
                movePage(1, true, query, newOrder);
              }}
              loading={loading}
              variant="subtle"
            >
              {ascending ? <LuArrowUp /> : <LuArrowDown />}
            </IconButton>
          </Tooltip>
          <Button onClick={handleBulkDiscountClick}>一括割引</Button>
        </Flex>
      )}
      {bulkActionMode.type === "discount" && (
        <BulkDiscountForm
          discountType={bulkActionMode.discountType}
          discountRate={bulkActionMode.discountRate}
          discountAmount={bulkActionMode.discountAmount}
          selectedItemIds={bulkActionMode.selectedItemIds}
          onDiscountTypeChange={(type) => setBulkActionMode((prev) => ({ ...prev, discountType: type }))}
          onDiscountRateChange={(rate) => setBulkActionMode((prev) => ({ ...prev, discountRate: rate }))}
          onDiscountAmountChange={(amount) => setBulkActionMode((prev) => ({ ...prev, discountAmount: amount }))}
          onSelectAllChange={(selected) => {
            const pageItemIds = data?.items.map((item) => item.id) ?? [];
            if (selected) {
              setBulkActionMode((prev) => {
                if (prev.type === "discount") {
                  return {
                    ...prev,
                    selectedItemIds: Array.from(new Set([...prev.selectedItemIds, ...pageItemIds])),
                  };
                }
                return prev;
              });
            } else {
              setBulkActionMode((prev) => {
                if (prev.type === "discount") {
                  return {
                    ...prev,
                    selectedItemIds: prev.selectedItemIds.filter((id: string) => !pageItemIds.includes(id)),
                  };
                }
                return prev;
              });
            }
          }}
          onSetDiscount={handleSetDiscount}
          isAllSelected={(() => {
            if (bulkActionMode.type !== "discount") return false;
            const pageItemIds = data?.items.map((item) => item.id) ?? [];
            return (
              pageItemIds.length > 0 && pageItemIds.every((id: string) => bulkActionMode.selectedItemIds.includes(id))
            );
          })()}
        />
      )}
      <VStack gap="4" align="stretch" mt={2}>
        {data?.items.map((item) => (
          <ListingCard
            key={item.id}
            item={item}
            showCheckbox={bulkActionMode.type === "discount"}
            checked={bulkActionMode.type === "discount" ? bulkActionMode.selectedItemIds.includes(item.id) : false}
            onCheckChange={(checked) => handleCheck(item.id, !!checked)}
          />
        ))}
        {error && <p>Error : {error}</p>}
        {data?.items.length ? (
          <VStack align="center" justify="center" mx={-8}>
            {bulkActionMode.type === "none" ? (
              <Flex gap={1} wrap="wrap" align="center" justify="center">
                {page > PAGE_BUTTON_RANGE + 1 && <Button onClick={() => movePage(1, true, query, ascending)}>1</Button>}
                {page > 1 && (
                  <Button onClick={() => movePage(page - 1, true, query, ascending)}>
                    <LuChevronLeft />
                  </Button>
                )}
                {Array.from({ length: totalPage })
                  .map((_, i) => i + 1)
                  .filter((to) => {
                    if (page <= PAGE_BUTTON_RANGE) {
                      return to <= page + PAGE_BUTTON_RANGE + (PAGE_BUTTON_RANGE - (page - 1));
                    }
                    return page - PAGE_BUTTON_RANGE <= to && to <= page + PAGE_BUTTON_RANGE;
                  })
                  .map((to) => (
                    <Button
                      key={to}
                      onClick={() => movePage(to, true, query, ascending)}
                      variant={page === to ? "solid" : "subtle"}
                    >
                      {to}
                    </Button>
                  ))}
                {page < totalPage && (
                  <Button onClick={() => movePage(page + 1, true, query, ascending)}>
                    <LuChevronRight />
                  </Button>
                )}
                {page < totalPage - PAGE_BUTTON_RANGE && (
                  <Button onClick={() => movePage(totalPage, true, query, ascending)}>{totalPage}</Button>
                )}
              </Flex>
            ) : (
              <Text color="red.500" fontSize="sm" mt={2}>
                まとめて値下げ中はページ移動できません
              </Text>
            )}
          </VStack>
        ) : null}
      </VStack>
      {bulkActionMode.type === "discount" && (
        <GeneralModal
          open={bulkActionMode.showConfirmModal}
          onClose={() => setBulkActionMode((prev) => ({ ...prev, showConfirmModal: false }))}
          title="値下げ確認"
          body={modalMessage()}
          mainAction={handleExecuteDiscount}
          mainActionLabel="実行する"
          loading={bulkActionMode.isProcessing}
        />
      )}
    </>
  );
}
